import React from 'react';
import ReactDOM from 'react-dom';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import Head from 'next/head';
import get from 'lodash.get';
import classNames from 'classnames';
import i18next from 'i18next';

import loadScript from 'lib/loadScript';
import { setTriggerTop as setTriggerTopAction } from 'redux/modules/layout';
import HeadlinePropType from 'lib/CustomPropTypes/headline';

import Ad from 'components/Ad';
import MiniPlayerTease from 'components/MiniPlayerTease';

import HeaderDefault from './Default';

import styles from './styles.module.scss';


const mapStateToProps = ({
  shared,
  navbar,
  article,
}) => ({
  vertical: shared.vertical,
  navbar,
  isNativeArticle: shared.isNativeArticle,
  sponsorAdEnabled: get(article, 'content[0].adsEnabled', true),
});

const mapActionsToProps = {
  setTriggerTop: setTriggerTopAction,
};

class ServiceHeader extends React.Component {
  // Holds queue of theme change functions until header scripts are ready
  queue = [];

  static propTypes = {
    header: PropTypes.shape({
      css: PropTypes.shape({}),
      js: PropTypes.shape({
        src: PropTypes.arrayOf(
          PropTypes.string,
        ),
      }),
      html: PropTypes.arrayOf(
        PropTypes.string,
      ),
    }).isRequired,
    vertical: PropTypes.string.isRequired,
    navbar: PropTypes.shape({
      theme: PropTypes.string,
      shareUrl: PropTypes.string,
      headline: HeadlinePropType,
    }).isRequired,
    setTriggerTop: PropTypes.func.isRequired,
    isNativeArticle: PropTypes.bool,
    sponsorAdEnabled: PropTypes.bool,
  };

  static defaultProps = {
    isNativeArticle: false,
    sponsorAdEnabled: true,
  };

  constructor(props) {
    super(props);
    this.state = {
      ready: false,
      sponsorAdRendered: false,
    };
  }

  componentDidMount() {
    if (this.hasHeader() && document) {
      const {
        header,
        vertical,
        navbar: {
          theme,
          shareUrl,
          headline,
        },
      } = this.props;

      const {
        js: {
          src = [],
        },
      } = header;

      // Set brand/vertical in config object
      if (window && typeof window.HFSconfig === 'undefined') {
        window.HFSapi = {};
        window.HFSconfig = {
          brand: vertical,
          header: {
            theme,
          },
        };
      }

      // Attach scripts
      src.forEach((script, index) => {
        loadScript(script)
          .then(() => {
            // Fire window event to initialize header
            if (index + 1 >= src.length) {
              const event = new CustomEvent('HFS.ready', { detail: 'header' });
              window.dispatchEvent(event);

              if (shareUrl && headline) {
                this.setShareUrlAndHeadline(shareUrl, headline);
              }

              // Indicate that script is ready
              this.ready();
            }
          });
      });

      // Observe height changes
      window.addEventListener('HFS.recalculateHeight', this.recalculateHeight);
    }
  }

  shouldComponentUpdate() {
    return this.hasHeader();
  }

  componentDidUpdate(prevProps, prevState) {
    const {
      active: prevActive,
      theme: prevTheme,
      headline: prevHeadline,
      shareUrl: prevShareUrl,
    } = prevProps.navbar || {};

    const {
      navbar: {
        active: currActive,
        theme: currTheme,
        headline: currHeadline,
        shareUrl: currShareUrl,
      } = {},
    } = this.props;

    const { ready } = this.state;

    // Update theme class on change
    if (prevTheme !== currTheme) {
      this.processApiFuncs(() => {
        window.HFSapi.updateTheme(currTheme);
      });
    }

    // Update active state
    if (prevActive !== currActive) {
      this.processApiFuncs(() => {
        window.HFSapi[!currActive ? 'deactivate' : 'activate']();
      });
    }

    // Update headline
    if (prevHeadline !== currHeadline) {
      this.processApiFuncs(() => {
        window.HFSapi.updateHeadline((currHeadline && currHeadline.primary) || null);
      });
    }

    // Update sharing links
    if (prevShareUrl !== currShareUrl) {
      this.setShareUrlAndHeadline(currShareUrl, currHeadline);
    }

    // Process queued func calls
    if (!prevState.ready && ready) {
      this.processApiFuncs();
    }
  }

  componentWillUnmount() {
    window.removeEventListener('HFS.recalculateHeight', this.recalculateHeight);
  }

  getSocialShareUrl = (path) => {
    const { vertical } = this.props;
    if (/^\//.test(path)) {
      switch (vertical) {
        case 'today':
          return `https://www.today.com${path}`;
        case 'msnbc':
          return `https://www.msnbc.com${path}`;
        case 'noticias':
          return `https://www.telemundo.com${path}`;
        default:
          return `https://www.nbcnews.com${path}`;
      }
    }
    return path;
  }

  setShareUrlAndHeadline = (shareUrl, headline) => {
    this.processApiFuncs(() => {
      window.HFSapi.updateSocial(
        this.getSocialShareUrl(shareUrl),
        (headline && headline.social) || null,
      );
    });
  }

  recalculateHeight = () => {
    if (this.wrapper) {
      const { setTriggerTop } = this.props;
      const { top } = this.wrapper.getBoundingClientRect();
      setTriggerTop(top + window.pageYOffset);
    }
  }

  hasHeader = () => {
    const { header } = this.props;
    return header && header.html && header.html.length;
  }

  hasGlobalInsert = () => {
    if (this.hasHeader()) {
      const {
        header: {
          html: [html],
        } = {},
      } = this.props;
      return html.indexOf('js-global-nav-insert') > -1;
    }
    return false;
  }

  onGlobalInsertShow = () => {
    this.processApiFuncs(() => {
      window.HFSapi.enableGlobalInsert();
    });
  }

  // Flag to indicate header scripts are ready
  ready = () => {
    this.setState({ ready: true });
  }

  // Queue or call header theme change functions
  processApiFuncs = (func) => {
    if (typeof func === 'function') {
      const { ready } = this.state;
      if (ready) {
        func.call();
      } else {
        this.queue.push(func);
      }
    } else {
      this.queue.forEach((f) => {
        f.call();
      });
    }
  }

  sponsorAd = () => {
    const { sponsorAdRendered } = this.state;
    const {
      vertical,
      isNativeArticle,
      sponsorAdEnabled,
      navbar: { theme },
    } = this.props;

    if (this.wrapper) {
      const portalSponsor = this.wrapper.querySelector('.js-sponsor-wrap');
      // Links to navbar pre-HFS
      // https://github.com/nbcnews/ramen/blob/0acf32d86f30bcb6567728dab0e1e2debdd1483c/src/components/Navbar/index.jsx#L399
      // https://github.com/nbcnews/ramen/blob/0acf32d86f30bcb6567728dab0e1e2debdd1483c/src/components/Navbar/styles.scss#L451
      if (portalSponsor && !isNativeArticle && sponsorAdEnabled && vertical !== 'sponsoredcontent') {
        return ReactDOM.createPortal(
          <div className={`ad-container v-hidden v-visible-l h-100 justify-center items-center ${sponsorAdRendered ? 'df' : 'dn'}`}>
            <span className={classNames([styles.sponsorText], {
              sponsorTextWhite: theme === 'light',
            })}
            >
              {i18next.t('Sponsored By')}
            </span>
            <Ad
              slot="sponsorlogo"
              handler={this.sponsorAdHandler}
              adClass="dib lh-none"
              refreshInterval={0}
            />
          </div>,
          portalSponsor,
        );
      }
    }
    return null;
  }

  sponsorAdHandler = (enigmaAd, { EVENTS }) => {
    enigmaAd.on(EVENTS.RENDER, (event) => {
      if (!event || event.empty) {
        return;
      }
      this.setState({
        sponsorAdRendered: true,
      });
    });
  }

  render() {
    if (!this.hasHeader()) {
      return <HeaderDefault />;
    }

    const {
      header: {
        css: { link: links },
        js: { html: scripts },
        html: [html],
      } = {},
      navbar: { theme },
    } = this.props;

    let portalRoot;
    if (this.wrapper) {
      portalRoot = this.wrapper.querySelector('.js-global-nav-insert');
    }

    return (
      <React.Fragment>
        {/* Attach stylesheets */}
        {Array.isArray(links) && links.length
          && (
            <Head>
              {links.map((href) => (
                <link key={href} href={href} rel="stylesheet" />
              ))}
            </Head>
          )}

        {/* Attach inline scripts */}
        {scripts && Array.isArray(scripts)
          && scripts.map((src) => (
            <script
              key={src.replace(/\s+/g, '').substr(0, 10)}
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: decodeURIComponent(src) }}
            />
          ))}

        {/* Insert header html markup */}
        <div
          id="hfs-header"
          className={classNames([styles.headerWrapper], {
            whiteTextOnOverlay: theme === 'light',
          })}
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: html }}
          ref={(element) => { this.wrapper = element; }}
        />

        {/* Render portal */}
        {this.hasGlobalInsert() && portalRoot
          && ReactDOM.createPortal(
            <MiniPlayerTease
              onShowTease={this.onGlobalInsertShow}
            />,
            portalRoot,
          )}

        {/* Render sponsorAd portal */}
        {this.sponsorAd()}
      </React.Fragment>
    );
  }
}

export default connect(mapStateToProps, mapActionsToProps)(ServiceHeader);
